import { type Dispatch, type FC, type SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { formatNumber } from "src/utils/numberFormat";
import {
  DefinitionTooltip,
  type DefinitionTooltipInfo,
} from "src/components/common";
import {
  META_TREND_AXIS_ID,
  normalizeTooltipValue,
  OrganicChartTooltip,
  type TrendGranularity,
  type TrendMetricKey,
} from "./metaChartConfig";
import { usePaidMediaChartRtl } from "../../utils/paidMediaChartRtl";

type MetaTrendChartRow = {
  date: string;
  spend: number;
  spendFormatted?: string;
  impressions: number;
  clicks: number;
  addToCart: number;
  conversions: number;
};

interface MetaPerformanceTrendsPanelProps {
  metaSectionHelp: (_key: string) => DefinitionTooltipInfo;
  trendGranularity: TrendGranularity;
  setTrendGranularity: Dispatch<SetStateAction<TrendGranularity>>;
  trendMetrics: ReadonlyArray<{ key: TrendMetricKey; label: string; color: string }>;
  activeTrendMetrics: TrendMetricKey[];
  toggleMetric: (_metric: TrendMetricKey) => void;
  paginatedTrendData: MetaTrendChartRow[];
  formatDateShortChart: (_value: string) => string;
  formatChartAxisCompactLocalized: (_value: unknown) => string;
  formatMoney: (
    _value: number,
    _options?: { minimumFractionDigits?: number; maximumFractionDigits?: number }
  ) => string;
  currentTrendPage: number;
  setCurrentTrendPage: Dispatch<SetStateAction<number>>;
  trendTotalPages: number;
}

const MetaPerformanceTrendsPanel: FC<MetaPerformanceTrendsPanelProps> = ({
  metaSectionHelp,
  trendGranularity,
  setTrendGranularity,
  trendMetrics,
  activeTrendMetrics,
  toggleMetric,
  paginatedTrendData,
  formatDateShortChart,
  formatChartAxisCompactLocalized,
  formatMoney,
  currentTrendPage,
  setCurrentTrendPage,
  trendTotalPages,
}) => {
  const { t, i18n } = useTranslation();
  const chartRtl = usePaidMediaChartRtl();
  // "Back" points towards the start of the reading direction, which is the right edge in Arabic.
  const PreviousIcon = chartRtl.isRTL ? ChevronRight : ChevronLeft;
  const NextIcon = chartRtl.isRTL ? ChevronLeft : ChevronRight;
  // Page counters are built client-side, so their digits have to be localized here — the rest of
  // the panel's numbers arrive pre-formatted from the API.
  const paginationLabel = t("pagination.pageOf", {
    current: formatNumber(currentTrendPage, {}, i18n.language),
    total: formatNumber(trendTotalPages, {}, i18n.language),
  });

  return (
    <article className="paid-media-panel paid-media-panel--trend">
      <div className="paid-media-panel__header paid-media-panel__header--trend">
        <div className="paid-media-panel__heading">
          <h3>{t("metaAdsReport.sections.performanceTrends")}</h3>
          <DefinitionTooltip
            label={t("metaAdsReport.sections.performanceTrends")}
            info={metaSectionHelp("performanceTrends")}
          />
        </div>
        <div className="paid-media-panel__header-actions paid-media-panel__header-actions--trend">
          <fieldset className="paid-media-trend-granularity">
            <legend className="paid-media-visually-hidden">
              {t("metaAdsReport.trends.granularityAria")}
            </legend>
            {(["daily", "monthly"] as const).map(option => (
              <button
                key={option}
                type="button"
                className={
                  trendGranularity === option
                    ? "paid-media-trend-granularity__btn paid-media-trend-granularity__btn--active"
                    : "paid-media-trend-granularity__btn"
                }
                onClick={() => setTrendGranularity(option)}
              >
                {t(`metaAdsReport.trends.${option}`)}
              </button>
            ))}
          </fieldset>
          <fieldset className="paid-media-trend-chips paid-media-trend-chips--legend">
            <legend className="paid-media-visually-hidden">
              {t("metaAdsReport.trends.metricsAria")}
            </legend>
            {trendMetrics.map(metric => (
              <button
                key={metric.key}
                type="button"
                className={
                  activeTrendMetrics.includes(metric.key)
                    ? "paid-media-trend-chips__item paid-media-trend-chips__item--active"
                    : "paid-media-trend-chips__item"
                }
                onClick={() => toggleMetric(metric.key)}
              >
                <span
                  className="paid-media-trend-chips__dot"
                  style={{ backgroundColor: metric.color }}
                />
                {metric.label}
              </button>
            ))}
          </fieldset>
        </div>
      </div>
      <div className="paid-media-chart paid-media-chart--trend">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={paginatedTrendData}>
            <CartesianGrid stroke="#dce3ef" strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              reversed={chartRtl.axisReversed}
              tickFormatter={formatDateShortChart}
              tick={{ fontSize: 11, fill: "#60718b" }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              yAxisId="primary"
              width={34}
              orientation={chartRtl.yAxisOrientation}
              tick={{ fontSize: 11, fill: "#60718b" }}
              axisLine={false}
              tickLine={false}
              tickFormatter={formatChartAxisCompactLocalized}
            />
            <YAxis
              yAxisId="secondary"
              orientation={chartRtl.oppositeYAxisOrientation}
              width={34}
              tick={{ fontSize: 11, fill: "#60718b" }}
              axisLine={false}
              tickLine={false}
              tickFormatter={formatChartAxisCompactLocalized}
            />
            <Tooltip
              content={({ active, label, payload }) => (
                <OrganicChartTooltip
                  active={active}
                  label={formatDateShortChart(String(label ?? ""))}
                  labelTitle={t("metaAdsReport.chart.date")}
                  payload={payload}
                  seriesLabelForDataKey={key =>
                    t(`metaAdsReport.metrics.${key as "spend" | "impressions" | "clicks" | "addToCart" | "conversions"}`)
                  }
                  valueFormatter={(value, name) => {
                    if (name === "spend") {
                      const row = paginatedTrendData.find(item => item.date === label);
                      return (
                        row?.spendFormatted ?? formatMoney(row?.spend ?? Number(value ?? 0))
                      );
                    }
                    return String(normalizeTooltipValue(value));
                  }}
                />
              )}
              wrapperStyle={{ pointerEvents: "none" }}
            />
            {trendMetrics
              .filter(metric => activeTrendMetrics.includes(metric.key))
              .map(metric => (
                <Line
                  key={metric.key}
                  type="monotone"
                  dataKey={metric.key}
                  name={metric.key}
                  yAxisId={META_TREND_AXIS_ID[metric.key]}
                  stroke={metric.color}
                  strokeWidth={2.4}
                  dot={false}
                  activeDot={{ r: 4 }}
                />
              ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
      <nav
        className="paid-media-trend-pagination"
        aria-label={paginationLabel}
      >
        <span className="paid-media-trend-pagination__label">{paginationLabel}</span>
        <div className="paid-media-trend-pagination__nav">
          <button
            type="button"
            className="paid-media-trend-pagination__btn"
            disabled={currentTrendPage <= 1}
            onClick={() => setCurrentTrendPage(page => Math.max(1, page - 1))}
            aria-label={t("pagination.previousPage")}
          >
            <PreviousIcon size={16} aria-hidden />
          </button>
          <button
            type="button"
            className="paid-media-trend-pagination__btn"
            disabled={currentTrendPage >= trendTotalPages}
            onClick={() =>
              setCurrentTrendPage(page => Math.min(trendTotalPages, page + 1))
            }
            aria-label={t("pagination.nextPage")}
          >
            <NextIcon size={16} aria-hidden />
          </button>
        </div>
      </nav>
    </article>
  );
};

export default MetaPerformanceTrendsPanel;
